import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET,
    PARTICULAR_REQUEST_PARAMETER_LIST_GET,
} from '../../../api/ApiConst';
import ApiCaller from '../../../api/ApiCaller';
import HeadersForm from './components/HeadersForm';
import RequestBodyForm from './components/RequestBodyForm';
import ResponseParametersForm from './components/ResponseParametersForm';
import UpdateConditionForm from './components/UpdateConditionForm';

// Constants
const OPERATORS = [
    { value: 'EQUAL_TO', label: 'EQUAL_TO' },
    { value: 'NOT_EQUAL_TO', label: 'NOT_EQUAL_TO' },
    { value: 'GREATER_THAN', label: 'GREATER_THAN' },
    { value: 'LESS_THAN', label: 'LESS_THAN' },
    { value: 'GREATER_THAN_EQUAL_TO', label: 'GREATER_THAN_EQUAL_TO' },
    { value: 'LESS_THAN_EQUAL_TO', label: 'LESS_THAN_EQUAL_TO' },
    { value: 'INCLUDES', label: 'INCLUDES' },
    { value: 'NOT_INCLUDES', label: 'NOT_INCLUDES' },
    { value: 'STARTS_WITH', label: 'STARTS_WITH' },
    { value: 'ENDS_WITH', label: 'ENDS_WITH' },
    { value: 'CONTAINS', label: 'CONTAINS' },
    { value: 'NOT_CONTAINS', label: 'NOT_CONTAINS' }
];

const ActionConfigModal = ({ 
    show, 
    action, 
    onSave, 
    onClose, 
    allTableList, 
    allSteps, 
    selectedRequest, 
    currentStepId 
}) => {
    // State Management
    const [formData, setFormData] = useState({});
    const [selectedTableColumns, setSelectedTableColumns] = useState([]);
    const [tableColumnsLoading, setTableColumnsLoading] = useState(false);
    const [allTableColumns, setAllTableColumns] = useState([]);
    const [remainingTableColumns, setRemainingTableColumns] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [requestBodyType, setRequestBodyType] = useState('none');
    const [requestBodyAttributes, setRequestBodyAttributes] = useState([]);
    const [requestParameters, setRequestParameters] = useState([]);
    const [allRequestParameters, setAllRequestParameters] = useState([]);
    const [requestParamSource, setRequestParamSource] = useState([]);
    const [responseParameters, setResponseParameters] = useState([]);
    const [allPreviousSteps, setAllPreviousSteps] = useState([]);
    const [allNextSteps, setAllNextSteps] = useState([]);
    const [conditionalSteps, setConditionalSteps] = useState([{ condition: '', stepId: '' }]);
    const [elseStepId, setElseStepId] = useState('');
    const [columnAttributes, setColumnAttributes] = useState({});

    // Helper Functions
    const getParameterSourcePrefix = (parameter) => {
        if (!parameter) return '';
        if (parameter.label?.includes('Custom Request')) return 'custom_request';
        const stepMatch = parameter.label?.match(/Step (\d+)/);
        if (stepMatch) return `step_${stepMatch[1]}`;
        return 'custom_request'; // fallback
    };

    const parseMappingString = (mappingValue) => {
        const mappingParts = mappingValue.split('.');
        return {
            sourcePrefix: mappingParts[0] || '',
            parameterName: mappingParts[1] || '',
            attributeName: mappingParts[2] || ''
        };
    };

    const buildMappingString = (sourcePrefix, parameterName, attributeName = '') => {
        const baseMapping = `${sourcePrefix}.${parameterName}`;
        return attributeName ? `${baseMapping}.${attributeName}` : baseMapping;
    };

    const findParameterByMapping = (mappingValue, availableParameters) => {
        const { sourcePrefix, parameterName } = parseMappingString(mappingValue);
        
        return availableParameters.find((parameter) => {
            const paramSourcePrefix = getParameterSourcePrefix(parameter);
            const paramName = parameter.label.split(' ')[0];
            return `${paramSourcePrefix}.${paramName}` === `${sourcePrefix}.${parameterName}`;
        });
    };

    const getParameterAttributes = (parameter) => {
        
        if (!parameter) {
            return [];
        }
        
        // Check if this is a step parameter (has the actual parameter object as value)
        if (parameter.value && typeof parameter.value === 'object' && parameter.value.attributes) {
            return parameter.value.attributes || [];
        }
        
        const foundParameter = allRequestParameters?.find(param => {
            return param?.id === parameter?.value;
        });
        
        return foundParameter?.attributes || [];
    };

    // Effects
    useEffect(() => {
        const previousSteps = allSteps.filter(step => currentStepId > step.id);
        const nextSteps = allSteps.filter(step => currentStepId < step.id);
        
        setAllPreviousSteps(previousSteps?.map(step => ({ 
            id: step.id, 
            action: step?.actions?.[0] || [] 
        })));
        setAllNextSteps(nextSteps?.map(step => ({ 
            id: step.id, 
            action: step?.actions?.[0] || [] 
        })));
    }, [allSteps, currentStepId]);

    useEffect(() => {
        if (action) {
            setFormData(action.config || {});
            setResponseParameters(action.config?.responseParameters || []);
            setRequestParamSource(action.config?.requestParamSource || ['custom_request']);
            setConditionalSteps(action.config?.conditionalSteps || [{ condition: '', stepId: '' }]);
            setElseStepId(action.config?.elseStepId || '');
            
            // Initialize column attributes from existing mappings immediately
            if (action.config?.mappings) {
                const initialColumnAttributes = {};
                const mappingKeys = Object.keys(action.config.mappings);
                mappingKeys.forEach(key => {
                    if (key.includes('.')) {
                        const parts = key.split('.');
                        if (parts.length >= 2) {
                            const columnName = parts[0];
                            const attributeName = parts[1];
                            initialColumnAttributes[columnName] = attributeName;
                        }
                    }
                });
                setColumnAttributes(initialColumnAttributes);
            } else {
                setColumnAttributes({});
            }

            if (action.config?.tableId) {
                fetchTableAttributes(action.config.tableId);
            }
        }
    }, [action]);

    useEffect(() => {
        requestParamSource.forEach(source => {
            if (source === 'custom_request') {
                fetchRequestParameters(selectedRequest);
            } else {
                loadStepParameters(source);
            }
        });
    }, [requestParamSource, selectedRequest, allPreviousSteps]);

    useEffect(() => {
        if (allTableColumns?.length > 0 && selectedTableColumns?.length > 0) {
            setRemainingTableColumns(allTableColumns?.filter(col => !selectedTableColumns?.includes(col)));
        } else {
            setRemainingTableColumns([]);
        }
    }, [allTableColumns, selectedTableColumns]);

    // Event Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        
        if (name === 'tableId') {
            fetchTableAttributes(value);
        }
    };

    const handleRequestParamSourceChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions?.map(option => option?.value) : ['custom_request'];
        setRequestParamSource(values);
        setFormData((prev) => ({
            ...prev,
            requestParamSource: values,
            mappings: {}, // reset mappings on source change
        }));
    };

    const handleAddTableColumn = () => {
        if (formData?.tableColumns?.length > 0) {
            const newColumn = allTableColumns?.find(col => col?.id === formData?.tableColumns);
            setSelectedTableColumns([...selectedTableColumns, newColumn]);
            setFormData((prev) => ({
                ...prev,
                tableColumns: "",
            }));
        }
    };

    const handleDeleteTableColumn = (col) => {
        setSelectedTableColumns(selectedTableColumns.filter(c => c !== col));
        
        // Also remove the mapping data for the deleted column
        const currentMappings = { ...formData.mappings } || {};
        if (currentMappings[col?.name]) {
            delete currentMappings[col.name];
            setFormData((prev) => ({
                ...prev,
                mappings: currentMappings,
            }));
        }
    };

    const handleParameterMappingChange = (selected, col) => {
        const currentMappings = { ...formData.mappings } || {};
        
        // Remove any existing mappings for this column
        if (currentMappings[col?.name]) {
            delete currentMappings[col.name];
        }
        
        // Also remove any existing column.attribute mappings for this column
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${col.name}.`)) {
                delete currentMappings[key];
            }
        });
        
        if (!selected) {
            // No selection, remove the column from selected columns
            setSelectedTableColumns(prev => prev.filter(c => c.name !== col.name));
            
            // Also clear the column attribute for this column
            setColumnAttributes(prev => {
                const newAttributes = { ...prev };
                delete newAttributes[col.name];
                return newAttributes;
            });
        } else {
            // Handle request parameter selection only
            const sourcePrefix = getParameterSourcePrefix(selected);
            const parameterName = selected.label.split(' ')[0];
            const parameterMapping = buildMappingString(sourcePrefix, parameterName);
            
            // Check if there's a column attribute selected
            const selectedColumnAttribute = columnAttributes[col.name];
            
            if (selectedColumnAttribute) {
                // Create mapping with column.attribute as key
                const mappingKey = `${col.name}.${selectedColumnAttribute}`;
                currentMappings[mappingKey] = parameterMapping;
            } else {
                // Create mapping with just column name as key
                currentMappings[col.name] = parameterMapping;
            }
        }
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleAttributeMappingChange = (selected, col) => {
        const currentMappings = { ...formData.mappings } || {};
        const attributeName = selected?.value || '';
        
        // Find the correct mapping key for this column
        let mappingKey = col.name;
        let mappingValue = currentMappings[col.name] || '';
        
        // Check if there's a column attribute mapping for this column
        const columnAttributeMappings = Object.keys(currentMappings).filter(key => key.startsWith(`${col.name}.`));
        if (columnAttributeMappings.length > 0) {
            // Use the column attribute mapping key
            mappingKey = columnAttributeMappings[0];
            mappingValue = currentMappings[mappingKey] || '';
        }
        
        const { sourcePrefix, parameterName } = parseMappingString(mappingValue);
        
        // Update the mapping at the correct key
        currentMappings[mappingKey] = buildMappingString(sourcePrefix, parameterName, attributeName);
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleColumnAttributeChange = (selected, col) => {
        const currentMappings = { ...formData.mappings } || {};
        const attributeName = selected?.value || '';
        
        // Find the existing parameter mapping for this column
        let existingParameterMapping = currentMappings[col.name];
        
        // Also check for any existing column.attribute mappings
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${col.name}.`)) {
                existingParameterMapping = currentMappings[key];
            }
        });
        
        // Remove the old mapping for this column
        if (currentMappings[col.name]) {
            delete currentMappings[col.name];
        }
        
        // Also remove any existing column.attribute mappings for this column
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${col.name}.`)) {
                delete currentMappings[key];
            }
        });
        
        // If there's an existing parameter mapping and an attribute is selected, create a new mapping
        if (existingParameterMapping && attributeName) {
            const newKey = `${col.name}.${attributeName}`;
            currentMappings[newKey] = existingParameterMapping;
        } else if (existingParameterMapping) {
            // If there's a parameter mapping but no attribute, keep the original mapping
            currentMappings[col.name] = existingParameterMapping;
        }
        
        setColumnAttributes(prev => ({
            ...prev,
            [col.name]: attributeName
        }));
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    // API Functions
    const fetchTableAttributes = (tableId) => {
        setTableColumnsLoading(true);
        const requestBody = {
            page: 0,
            size: 100,
            searchFilters: [],
            sortRules: [],
        };
        const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', tableId);

                    ApiCaller.callPostApi(
                apiUrl,
                requestBody,
                {},
                async (response) => {
                    let formAttributes = response?.data?.filter(item => item.formAttribute === true);
                    // Fetch referenced table attributes for attributes that have referenceTable
                    formAttributes = await fetchReferencedTableAttributes(formAttributes);
                    
                    // Pass the existing mappings from action.config if available
                    const existingMappings = action?.config?.mappings || null;
                    initializeSelectedColumns(formAttributes, existingMappings);
                    setAllTableColumns(formAttributes);
                    setTableColumnsLoading(false);
                },
            (error) => {
                console.error('Error fetching table attributes:', error);
                setSelectedTableColumns([]);
                setTableColumnsLoading(false);
            }
        );
    };

    const fetchReferencedTableAttributes = async (formAttributes) => {
        if (!formAttributes || formAttributes.length === 0) return formAttributes;

        const enhancedAttributes = [...formAttributes];

        for (let i = 0; i < enhancedAttributes.length; i++) {
            const attribute = enhancedAttributes[i];
            
            // Check if this attribute has a reference table
            if (attribute.referenceTable && attribute.referenceTable !== null) {
                try {
                    const referencedAttributes = await fetchReferencedTableAttributesById(attribute.referenceTable);
                    if (referencedAttributes && referencedAttributes.length > 0) {
                        // Add the referenced attributes to the current attribute
                        enhancedAttributes[i] = {
                            ...attribute,
                            attributes: referencedAttributes
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching referenced table attributes for ${attribute.name}:`, error);
                }
            }
        }

        return enhancedAttributes;
    };

    const fetchReferencedTableAttributesById = (referenceTableId) => {
        return new Promise((resolve, reject) => {
            const requestBody = {
                page: 0,
                size: 100,
                searchFilters: [],
                sortRules: [],
            };
            const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', referenceTableId);

            ApiCaller.callPostApi(
                apiUrl,
                requestBody,
                {},
                (response) => {
                    const referencedAttributes = response?.data?.filter(item => item.formAttribute === true) || [];
                    resolve(referencedAttributes);
                },
                (error) => {
                    console.error('Error fetching referenced table attributes:', error);
                    reject(error);
                }
            );
        });
    };

    const initializeSelectedColumns = (formAttributes, existingMappings = null) => {
        const mappings = existingMappings || formData?.mappings || {};
        
        if (Object.keys(mappings).length > 0) {
            const mappingKeys = Object.keys(mappings);
            
            // Collect all unique column names from mappings
            const columnsWithMappings = new Set();
            
            mappingKeys.forEach(key => {
                const mappingValue = mappings[key];
                
                if (mappingValue && mappingValue.trim()) {
                    let columnName;
                    if (key.includes('.')) {
                        // This is a column.attribute key, extract the column name
                        columnName = key.split('.')[0];
                    } else {
                        // This is a direct column name
                        columnName = key;
                    }
                    
                    // Add to the set to avoid duplicates
                    columnsWithMappings.add(columnName);
                }
            });
            
            // Convert set to array and filter formAttributes
            const columnsWithMappingsArray = Array.from(columnsWithMappings);
            const selectedColumnsData = formAttributes?.filter(item => columnsWithMappingsArray.includes(item.name));
            setSelectedTableColumns(selectedColumnsData);
            
            // Initialize column attributes from existing mappings
            setColumnAttributes(prev => {
                const initialColumnAttributes = { ...prev };
                mappingKeys.forEach(key => {
                    if (key.includes('.')) {
                        const parts = key.split('.');
                        if (parts.length >= 2) {
                            const columnName = parts[0];
                            const attributeName = parts[1];
                            if (!initialColumnAttributes[columnName]) {
                                initialColumnAttributes[columnName] = attributeName;
                            }
                        }
                    }
                });
                return initialColumnAttributes;
            });
        } else {
            // For new workflow steps (no existing mappings), show 3 default rows
            setSelectedTableColumns(formAttributes?.slice(0, 3) || []);
        }
    };

    const fetchRequestParameters = (requestId) => {
        ApiCaller.callPostApi(
            PARTICULAR_REQUEST_PARAMETER_LIST_GET.replace('{request-id}', requestId),
            null,
            {},
            (response) => {
                if (response?.data?.length > 0) {
                    const customRequestParams = response.data.map((item) => ({
                        value: item.id,
                        label: `${item.name} (Custom Request)`,
                    }));

                    const stepParams = getStepParameters();
                    setRequestParameters([...customRequestParams, ...stepParams]);
                    setAllRequestParameters(response.data);
                } else {
                    setRequestParameters(getStepParameters());
                }
            },
            (error) => {
                console.error('Error fetching request parameters:', error);
                setRequestParameters(getStepParameters());
            }
        );
    };

    const loadStepParameters = (source) => {
        const stepAttributes = allPreviousSteps?.find(step => step.id === source)?.action?.config?.responseParameters;
        const stepParams = stepAttributes?.map(param => ({
            value: param,
            label: `${param?.name} (Step ${source})`
        })) || [];
        setRequestParameters(stepParams);
    };

    const getStepParameters = () => {
        let stepParams = [];
        requestParamSource.forEach(source => {
            if (source !== 'custom_request') {
                const stepAttributes = allPreviousSteps?.find(step => step.id === source)?.action?.config?.responseParameters;
                if (stepAttributes) {
                    stepParams = [...stepParams, ...stepAttributes.map(param => ({
                        value: param,
                        label: `${param?.name || param} (Step ${source})`
                    }))];
                }
            }
        });
        return stepParams;
    };

    // Validation Functions
    const validateTableSelection = () => {
        if (!formData?.tableId) {
            toast.error('Please select a table');
            return false;
        }
        return true;
    };

    const validateMappings = () => {
        if (!formData?.mappings || Object.keys(formData.mappings).length === 0) {
            toast.error('Please map at least one JSON field');
            return false;
        }

        let hasValidMapping = false;
        for (let key in formData.mappings) {
            const mappingValue = formData.mappings[key];
            if (mappingValue && mappingValue.trim()) {
                const { sourcePrefix, parameterName, attributeName } = parseMappingString(mappingValue);
                
                if (sourcePrefix && parameterName) {
                    if (sourcePrefix === 'table') {
                        // Handle table column mapping
                        const selectedTableColumn = selectedTableColumns.find(column => column.name === parameterName);
                        if (selectedTableColumn?.attributes?.length > 0) {
                            // If table column has attributes, both column and attribute must be selected
                            if (attributeName) {
                                hasValidMapping = true;
                            }
                        } else {
                            // If table column has no attributes, only column is required
                            hasValidMapping = true;
                        }
                    } else {
                        // Handle request parameter mapping
                        const selectedParameter = findParameterForValidation(sourcePrefix, parameterName);
                        
                        if (selectedParameter?.attributes?.length > 0) {
                            // If parameter has attributes, both parameter and attribute must be selected
                            if (attributeName) {
                                hasValidMapping = true;
                            }
                        } else {
                            // If parameter has no attributes, only parameter is required
                            hasValidMapping = true;
                        }
                    }
                }
            }
        }
        
        if (!hasValidMapping) {
            toast.error('Please map at least one JSON field');
            return false;
        }
        
        return true;
    };

    const findParameterForValidation = (sourcePrefix, parameterName) => {
        return allRequestParameters?.find(param => {
            const paramSourcePrefix = getParameterSourcePrefix({
                label: param.name + (sourcePrefix === 'custom_request' ? ' (Custom Request)' : ` (Step ${sourcePrefix.replace('step_', '')})`)
            });
            return paramSourcePrefix === sourcePrefix && param.name === parameterName;
        });
    };

    const validateUpdateCondition = () => {
        if (!formData?.conditionField || !formData?.conditionOperator || !formData?.conditionValue) {
            toast.error('Update condition is required');
            return false;
        }
        
        const condition = `${formData.conditionField} ${formData.conditionOperator} ${formData.conditionValue}`;
        setFormData(prev => ({ ...prev, condition }));
        return true;
    };

    const validateDeleteCondition = () => {
        if (!formData?.condition) {
            toast.error('Delete condition is required');
            return false;
        }
        return true;
    };

    const validateApiConfiguration = () => {
        if (!formData?.method || !formData?.endpoint) {
            toast.error('API method and endpoint are required');
            return false;
        }
        
        if (requestBodyType !== 'none' && requestBodyAttributes?.length === 0) {
            toast.error('Please add at least one request body attribute');
            return false;
        }
        
        return true;
    };

    const validateEmailConfiguration = () => {
        if (!formData?.recipient?.trim()) {
            toast.error('Recipient email is required');
            return false;
        }
        
        if (!formData?.subject?.trim()) {
            toast.error('Subject is required');
            return false;
        }
        
        return true;
    };

    const validateConditionalSteps = () => {
        if (conditionalSteps.some((step) => !step.condition?.trim()?.length)) {
            toast.error('All conditional steps must have a condition');
            return false;
        }
        
        if (!elseStepId?.trim()?.length) {
            toast.error('Else step is required');
            return false;
        }
        
        return true;
    };

    const handleSubmit = () => {
        let isValid = true;

        switch (action.id) {
            case 'INSERT':
            case 'UPDATE':
                if (!validateTableSelection()) {
                    isValid = false;
                } else if (!validateMappings()) {
                    isValid = false;
                } else if (action.id === 'UPDATE' && !validateUpdateCondition()) {
                    isValid = false;
                }
                break;

            case 'DELETE':
                if (!validateTableSelection()) {
                    isValid = false;
                } else if (!validateDeleteCondition()) {
                    isValid = false;
                }
                break;

            case 'CALL_API':
                if (!validateApiConfiguration()) {
                    isValid = false;
                }
                break;

            case 'SEND_EMAIL':
                if (!validateEmailConfiguration()) {
                    isValid = false;
                }
                break;

            case 'CONDITION':
                if (!validateConditionalSteps()) {
                    isValid = false;
                }
                break;

            default:
                break;
        }

        if (!isValid) return;

        onSave({ ...action, config: formData });
    };

    // Render Components
    const renderTableSelector = () => (
        <Form.Group>
            <Form.Label>Table</Form.Label>
            <Form.Control 
                as="select" 
                name="tableId" 
                value={formData?.tableId || ''} 
                onChange={handleChange}
            >
                <option value="">Select Table</option>
                {allTableList?.map((table) => (
                    <option key={table?.id} value={table?.id}>
                        {table?.name}
                    </option>
                ))}
            </Form.Control>
        </Form.Group>
    );

    const renderRequestParameterSource = () => (
        <Form.Group className="mt-3">
            <Form.Label>Request Parameter Source</Form.Label>
            <Select
                isMulti
                options={[
                    { value: 'custom_request', label: 'Custom Request' },
                    ...(allPreviousSteps || [])?.map((step) => ({ 
                        value: step.id, 
                        label: "Step " + step.id 
                    })),
                ]}
                value={
                    requestParamSource
                        ? requestParamSource?.map(source =>
                            source === 'custom_request'
                                ? { value: 'custom_request', label: 'Custom Request' }
                                : allPreviousSteps
                                    ?.map((step) => ({ value: step.id, label: "Step " + step.id }))
                                    ?.find((opt) => opt.value === source)
                        ).filter(Boolean)
                        : []
                }
                onChange={handleRequestParamSourceChange}
                isClearable={false}
                className="react-select-container"
            />
        </Form.Group>
    );

    const renderTableColumnSelector = () => (
        <div className="d-flex align-items-end mb-4">
            <Form.Group style={{ width: '55%' }}>
                <Form.Label>Table Columns</Form.Label>
                <Form.Control 
                    as="select" 
                    name="tableColumns" 
                    value={formData?.tableColumns || ''} 
                    onChange={handleChange}
                >
                    <option value="">Select Table Column</option>
                    {remainingTableColumns?.map((col) => (
                        <option key={col?.id} value={col?.id}>{col?.name}</option>
                    ))}
                </Form.Control>
            </Form.Group>
            <Form.Group className="mx-4">
                <Button variant="primary" onClick={handleAddTableColumn}>
                    Add
                </Button>
            </Form.Group>
        </div>
    );

    const renderParameterSelect = (col, selectedParameter) => {
        // Only show request parameters (custom request or steps), not table columns
        const allOptions = [...requestParameters];
        
        // Get attributes for the selected parameter
        const parameterAttributes = getParameterAttributes(selectedParameter);
        const hasParameterAttributes = parameterAttributes?.length > 0;
        
        // Get the current attribute name from the mapping
        // Check for both direct column mapping and column.attribute mappings
        let mappingValue = formData?.mappings?.[col.name] || '';
        let columnAttributeMappings = Object.keys(formData?.mappings || {}).filter(key => key.startsWith(`${col.name}.`));
        
        // If there are column.attribute mappings, use the first one
        if (columnAttributeMappings.length > 0) {
            mappingValue = formData?.mappings?.[columnAttributeMappings[0]] || '';
        }
        
        const { attributeName } = parseMappingString(mappingValue);

        return (
            <div className="mb-2">
                <Form.Label required className="fw-bold">Select Parameter</Form.Label>
                <Select
                    options={allOptions}
                    isClearable
                    value={selectedParameter}
                    onChange={(selected) => handleParameterMappingChange(selected, col)}
                    placeholder="Select Parameter"
                    className="react-select-container"
                />
                
                {/* Show parameter attributes if available */}
                {hasParameterAttributes && (
                    <div className="mt-2">
                        <Form.Label className="small text-muted">Select Parameter Attribute</Form.Label>
                        <Select
                            options={parameterAttributes.map(attr => ({
                                value: attr?.name || attr,
                                label: attr?.name || attr
                            }))}
                            isClearable
                            value={attributeName ? { value: attributeName, label: attributeName } : null}
                            onChange={(selected) => handleAttributeMappingChange(selected, col)}
                            placeholder="Select Parameter Attribute"
                            className="react-select-container"
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderAttributeSelect = (col, attributes, attributeName, attributeSource) => (
        <div>
            <Form.Label required className="fw-bold">
                Select {attributeSource === 'parameter' ? 'Parameter' : 'Table'} Attribute
            </Form.Label>
            <Select
                options={attributes?.map(attr => ({
                    value: attr?.name,
                    label: attr?.name
                }))}
                isClearable
                value={attributeName ? { value: attributeName, label: attributeName } : null}
                onChange={(selected) => handleAttributeMappingChange(selected, col)}
                placeholder={`Select ${attributeSource === 'parameter' ? 'Parameter' : 'Table'} Attribute`}
                className="react-select-container"
            />
            <small className="text-muted">
                Both {attributeSource === 'parameter' ? 'parameter' : 'table column'} and attribute are required for this mapping.
            </small>
        </div>
    );

    const renderMappingRow = (col, idx) => {
        // Check for both direct column mapping and column.attribute mappings
        let mappingValue = formData?.mappings?.[col.name] || '';
        let columnAttributeMappings = Object.keys(formData?.mappings || {}).filter(key => key.startsWith(`${col.name}.`));
        
        // If there are column.attribute mappings, use the first one
        if (columnAttributeMappings.length > 0) {
            mappingValue = formData?.mappings?.[columnAttributeMappings[0]] || '';
        }
        
        const { sourcePrefix, parameterName, attributeName } = parseMappingString(mappingValue);
        
        // Find the selected parameter based on the mapping string
        let selectedParameter = null;

        if (sourcePrefix !== 'table') {
            // This is a request parameter mapping
            selectedParameter = requestParameters.find((parameter) => {
                const paramSourcePrefix = getParameterSourcePrefix(parameter);
                const paramName = parameter.label.split(' ')[0];
                return `${paramSourcePrefix}.${paramName}` === `${sourcePrefix}.${parameterName}`;
            });
        }


        
        // Get attributes for the selected parameter (request parameter attributes)
        const parameterAttributes = getParameterAttributes(selectedParameter);
        const hasParameterAttributes = parameterAttributes?.length > 0;
        
        // Create the option for the select
        const selectedOption = selectedParameter;
        
        return (
            <div className="d-flex align-items-start mb-3 p-3 border rounded" key={idx}>
                <div style={{ width: '30%',paddingRight:"5%" }}>
                    <strong className="text-primary">{col?.name}</strong>
                    {/* Show attribute selector for the table column itself if it has attributes */}
                    {col.attributes && col.attributes.length > 0 && (
                        <div className="mt-2">
                            {/* <Form.Label className="small text-muted">Select Column Attribute</Form.Label> */}
                            <Select
                                options={col.attributes.map(attr => ({
                                    value: attr?.name || attr,
                                    label: attr?.name || attr
                                }))}
                                isClearable
                                value={columnAttributes[col.name] ? { value: columnAttributes[col.name], label: columnAttributes[col.name] } : null}
                                onChange={(selected) => handleColumnAttributeChange(selected, col)}
                                placeholder="Select Column Attribute"
                                className="react-select-container"
                            />
                        </div>
                    )}
                </div>
                <div style={{ width: '60%' }}>
                    {renderParameterSelect(col, selectedOption)}
                </div>
                <div style={{ width: '10%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', paddingTop: '8px' }}>
                    <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => handleDeleteTableColumn(col)}
                    >
                        <i className="fa fa-trash"></i>
                    </Button>
                </div>
            </div>
        );
    };

    const renderMappings = () => (
        <>
            <Form.Label><b>Map Table Columns to Request parameters</b></Form.Label>
            {renderTableColumnSelector()}

            {tableColumnsLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                selectedTableColumns?.map((col, idx) => renderMappingRow(col, idx))
            )}
        </>
    );

    const renderInsertUpdateFields = () => (
        <>
            {renderTableSelector()}
            {renderRequestParameterSource()}
            <hr style={{ margin: "20px 0 30px" }} />
            <Form.Group className="mt-3">{renderMappings()}</Form.Group>

            {action.id === 'UPDATE' && (
                <>
                    <hr style={{ margin: "20px 0 30px" }} />
                    <UpdateConditionForm
                        formData={formData}
                        setFormData={setFormData}
                        requestParameters={requestParameters}
                    />
                </>
            )}
            <hr style={{ margin: "20px 0 30px" }} />
            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );

    const renderDeleteFields = () => (
        <>
            {renderTableSelector()}
            {renderRequestParameterSource()}
            <hr />
            <Form.Group className="mt-3">
                <Form.Label><b>Delete Condition</b></Form.Label>
                <div className="d-flex align-items-end gap-2">
                    <div className="flex-grow-1">
                        <Form.Label className="small text-muted">Field</Form.Label>
                        <Select
                            options={requestParameters}
                            value={requestParameters.find(param => param.value === formData?.conditionField?.value)}
                            onChange={(selected) => {
                                setFormData(prev => ({
                                    ...prev,
                                    conditionField: selected || '',
                                    condition: `${selected?.label || ''} ${prev.conditionOperator || ''} ${prev.conditionValue || ''}`
                                }));
                            }}
                            placeholder="Select Field"
                            className="react-select-container"
                        />
                    </div>

                    <div style={{ width: '200px' }}>
                        <Form.Label className="small text-muted">Operator</Form.Label>
                        <Select
                            options={OPERATORS}
                            value={OPERATORS.find(op => op.value === formData?.conditionOperator)}
                            onChange={(selected) => {
                                setFormData(prev => ({
                                    ...prev,
                                    conditionOperator: selected?.value || '',
                                    condition: `${prev.conditionField?.label || ''} ${selected?.label || ''} ${prev.conditionValue || ''}`
                                }));
                            }}
                            placeholder="Select Operator"
                            className="react-select-container"
                        />
                    </div>

                    <div className="flex-grow-1">
                        <Form.Label className="small text-muted">Value</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData?.conditionValue || ''}
                            onChange={(e) => {
                                setFormData(prev => ({
                                    ...prev,
                                    conditionValue: e.target.value,
                                    condition: `${prev.conditionField?.label || ''} ${prev.conditionOperator || ''} ${e.target.value}`
                                }));
                            }}
                            placeholder="Enter Value"
                        />
                    </div>
                </div>
            </Form.Group>
        </>
    );

    const renderApiFields = () => (
        <>
            <Form.Group>
                <Form.Label>Method</Form.Label>
                <Form.Control 
                    as="select" 
                    name="method" 
                    value={formData?.method || ''} 
                    onChange={handleChange}
                >
                    <option value="">Select</option>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                </Form.Control>
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Label>Endpoint</Form.Label>
                <Form.Control 
                    type="text" 
                    name="endpoint" 
                    value={formData?.endpoint || ''} 
                    onChange={handleChange} 
                />
            </Form.Group>
            {renderRequestParameterSource()}

            <HeadersForm
                headers={headers}
                setHeaders={setHeaders}
                formData={formData}
                setFormData={setFormData}
            />

            <RequestBodyForm
                requestBodyType={requestBodyType}
                setRequestBodyType={setRequestBodyType}
                requestBodyAttributes={requestBodyAttributes}
                setRequestBodyAttributes={setRequestBodyAttributes}
                formData={formData}
                setFormData={setFormData}
                requestParameters={requestParameters}
            />

            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );

    const renderEmailFields = () => (
        <>
            <Form.Group>
                <Form.Label>Recipient Email</Form.Label>
                <Form.Control
                    type="email"
                    name="recipient"
                    value={formData?.recipient || ''}
                    onChange={handleChange}
                    placeholder="recipient@example.com"
                />
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                    type="text"
                    name="subject"
                    value={formData?.subject || ''}
                    onChange={handleChange}
                />
            </Form.Group>
            <Form.Group className="mt-3">
                <Form.Label>Body</Form.Label>
                <Form.Control
                    as="textarea"
                    name="body"
                    value={formData?.body || ''}
                    onChange={handleChange}
                    rows={5}
                />
            </Form.Group>

            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );

    const renderConditionFields = () => (
        <>
            {renderRequestParameterSource()}
            <hr />
            <div className="mb-3 border p-3 rounded">
                <Form.Label><b>If Condition</b></Form.Label>
                <div className="d-flex align-items-end gap-2">
                    <div className="flex-grow-1">
                        <Form.Label className="small text-muted">Field</Form.Label>
                        <Select
                            options={requestParameters}
                            value={requestParameters.find(param => param.value === conditionalSteps[0]?.conditionField?.value)}
                            onChange={(selected) => {
                                const newConditions = [...conditionalSteps];
                                newConditions[0] = {
                                    ...newConditions[0],
                                    conditionField: selected || '',
                                    condition: `${selected?.label || ''} ${newConditions[0]?.conditionOperator || ''} ${newConditions[0]?.conditionValue || ''}`
                                };
                                setConditionalSteps(newConditions);
                                setFormData((prev) => ({
                                    ...prev,
                                    conditionalSteps: newConditions,
                                }));
                            }}
                            placeholder="Select Field"
                            className="react-select-container"
                        />
                    </div>

                    <div style={{ width: '200px' }}>
                        <Form.Label className="small text-muted">Operator</Form.Label>
                        <Select
                            options={OPERATORS}
                            value={OPERATORS.find(op => op.value === conditionalSteps[0]?.conditionOperator)}
                            onChange={(selected) => {
                                const newConditions = [...conditionalSteps];
                                newConditions[0] = {
                                    ...newConditions[0],
                                    conditionOperator: selected?.value || '',
                                    condition: `${newConditions[0]?.conditionField?.label || ''} ${selected?.label || ''} ${newConditions[0]?.conditionValue || ''}`
                                };
                                setConditionalSteps(newConditions);
                                setFormData((prev) => ({
                                    ...prev,
                                    conditionalSteps: newConditions,
                                }));
                            }}
                            placeholder="Select Operator"
                            className="react-select-container"
                        />
                    </div>

                    <div className="flex-grow-1">
                        <Form.Label className="small text-muted">Value</Form.Label>
                        <Form.Control
                            type="text"
                            value={conditionalSteps[0]?.conditionValue || ''}
                            onChange={(e) => {
                                const newConditions = [...conditionalSteps];
                                newConditions[0] = {
                                    ...newConditions[0],
                                    conditionValue: e.target.value,
                                    condition: `${newConditions[0]?.conditionField?.label || ''} ${newConditions[0]?.conditionOperator || ''} ${e.target.value}`
                                };
                                setConditionalSteps(newConditions);
                                setFormData((prev) => ({
                                    ...prev,
                                    conditionalSteps: newConditions,
                                }));
                            }}
                            placeholder="Enter Value"
                        />
                    </div>
                </div>
            </div>

            <Form.Group className="mt-3">
                <Form.Label>Else Step</Form.Label>
                <Form.Control
                    as="select"
                    value={elseStepId}
                    onChange={(e) => {
                        setElseStepId(e.target.value);
                        setFormData((prev) => ({
                            ...prev,
                            elseStepId: e.target.value,
                        }));
                    }}
                >
                    <option value="">Select Step</option>
                    {allSteps
                        ?.filter(step => step.id > currentStepId)
                        ?.map((step) => (
                            <option key={step.id} value={step.id}>
                                Step {step.id}
                            </option>
                        ))}
                </Form.Control>
            </Form.Group>
        </>
    );

    const renderFields = () => {
        switch (action?.id) {
            case 'INSERT':
            case 'UPDATE':
                return renderInsertUpdateFields();

            case 'DELETE':
                return renderDeleteFields();

            case 'CALL_API':
                return renderApiFields();

            case 'SEND_EMAIL':
                return renderEmailFields();

            case 'CONDITION':
                return renderConditionFields();

            default:
                return <div>No configuration available for this action.</div>;
        }
    };

    // Main Component Render
    return (
        <Modal 
            show={show} 
            onHide={onClose} 
            size="lg" 
            centered 
            backdrop="static" 
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>Configure Action - {action?.label}</Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                {renderFields()}
            </Modal.Body>
            
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ActionConfigModal;

export const renderActionConfigDetails = (action, allTableList) => {
    if (!action?.config) return null;

    const config = action.config;
    const selectedTable = allTableList?.find((table) => table?.id === config?.tableId);

    const renderMappingList = (mapping) => {
        if (!mapping || Object.keys(mapping)?.length === 0) {
            return <span style={{ fontStyle: 'italic', color: '#999' }}>None</span>;
        }

        return (
            <ul style={{ marginTop: '4px', marginBottom: '4px' }}>
                {Object.entries(mapping || {})?.map(([key, val]) => {
                    if (typeof val === 'object' && val !== null) {
                        // Old structure with parameterId and attributeName
                        const displayValue = val.attributeName 
                            ? `${val.parameterLabel}.${val.attributeName}`
                            : val.parameterLabel;
                        return (
                            <li key={key}>
                                <code>{key}</code> → <code>{displayValue}</code>
                            </li>
                        );
                    } else {
                        // New string structure (e.g., "custom_request.name.id" or "table.columnName.attributeName")
                        return (
                            <li key={key}>
                                <code>{key}</code> → <code>{val}</code>
                            </li>
                        );
                    }
                })}
            </ul>
        );
    };

    const renderResponseParameters = (responseParameters) => {
        if (!responseParameters?.length > 0) {
            return <span style={{ fontStyle: 'italic', color: '#999' }}>None</span>;
        }

        return (
            <div style={{ marginTop: '4px', marginBottom: '4px', display: 'flex', flexWrap: 'wrap' }}>
                {responseParameters?.map((param, index) => (
                    <div key={param?.id || index} style={{ marginRight: '4px', marginBottom: '4px' }} className='badge bg-primary'>
                        {param?.name || index}
                    </div>
                ))}
            </div>
        );
    };

    const renderConditionalSteps = (conditionalSteps) => {
        if (!conditionalSteps || conditionalSteps?.length === 0) {
            return <span style={{ fontStyle: 'italic', color: '#999' }}>None</span>;
        }
        return conditionalSteps?.map((step, index) => (
            <div key={index}>
                <div><b>Condition:</b> {step.condition || <i style={{ color: '#999' }}>N/A</i>}</div>
                <div><b>Render Step:</b> {step.stepId || <i style={{ color: '#999' }}>N/A</i>}</div>
            </div>
        ));
    };

    const parameterSourceLabel = (source) => {
        if (!source || source === 'custom_request') return 'Custom Request';
        if (source.startsWith('step')) return `Step ${source.replace('step', '')}`;
        return source;
    };

    switch (action.id) {
        case 'INSERT':
        case 'UPDATE':
            return (
                <>
                    <div><b>Table:</b> {selectedTable?.name || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Parameter Source:</b> {parameterSourceLabel(config?.parameterSource)}</div>
                    <div><b>Request Field Mappings:</b> {renderMappingList(config?.mappings)}</div>
                    {action.id === 'UPDATE' && (
                        <div>
                            <b>Update Condition:</b> <br />
                            <div className='d-flex align-items-center gap-2'>
                                <div>
                                    {config?.conditionField?.label}
                                </div>
                                <div>
                                    {config?.conditionOperator}
                                </div>
                                <div>
                                    {config?.conditionValue}
                                </div>
                            </div>
                        </div>
                    )}
                    <div><b>Response Parameters:</b> {renderResponseParameters(config?.responseParameters)}</div>
                </>
            );

        case 'DELETE':
            return (
                <>
                    <div><b>Table:</b> {selectedTable?.name || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Condition:</b> {config?.condition || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Response Parameters:</b> {renderResponseParameters(config?.responseParameters)}</div>
                </>
            );

        case 'CALL_API':
            return (
                <>
                    <div><b>Method:</b> {config?.method || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Endpoint:</b> {config?.endpoint || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Request Mapping:</b> {renderMappingList(config?.requestMapping)}</div>
                    <div><b>Response Parameters:</b> {renderResponseParameters(config?.responseParameters)}</div>
                </>
            );

        case 'SEND_EMAIL':
            return (
                <>
                    <div><b>Recipient:</b> {config?.recipient || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Subject:</b> {config?.subject || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Body:</b></div>
                    <div style={{
                        whiteSpace: 'pre-wrap',
                        backgroundColor: '#f5f5f5',
                        padding: '8px',
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                    }}>
                        {config?.body || <i style={{ color: '#999' }}>No content</i>}
                    </div>
                    <div className="mt-2"><b>Response Parameters:</b> {renderResponseParameters(config?.responseParameters)}</div>
                </>
            );

        case 'CONDITION':
            return (
                <>
                    <div><b>Main Condition Logic:</b> {config?.conditionLogic || <i style={{ color: '#999' }}>N/A</i>}</div>
                    <div><b>Conditional Steps:</b> {renderConditionalSteps(config?.conditionalSteps)}</div>
                    <div><b>Else Step:</b> {config?.elseStepId || <i style={{ color: '#999' }}>N/A</i>}</div>
                </>
            );

        default:
            return null;
    }
};



